import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createBlockResponse,
  createCriterionResponse,
  getRun,
  listBlockResponses,
  listCriterionResponses,
  listInstrumentBlocks,
  listInstrumentCriteria,
  listScaleAnchors,
  submitSelfAssessment,
  updateCriterionResponse,
  type ScaleLevel,
} from "../lib/api";
import { clientError } from "../lib/clientMessage";
import { isAnswering, isSubmitted } from "../lib/respondent";
import { UNSCORED, answerOf, examplesOf, observationWindow, patchFor, type SnapshotAnswer } from "../lib/snapshot";
import type { BlockResponse, CriterionResponse, InstrumentBlock, InstrumentCriterion, Run } from "../lib/types";

// The Transformation Snapshot questionnaire (SPECIFICATION_TRANSFORMATION_SURVEYS
// 011126 V1.0.md, slice 1): 12 questions about the last four weeks, one at a
// time, eight answers each, saved on the tap. Five answers are scored 1-5; three
// are unscored and are stored in the states the database already has
// (lib/snapshot.ts). No free text, no area "not applicable", no PMO lines.
// Slice 2 (DESIGN_SLICE_SNAPSHOT_SLICE_2 011126 V1.0.md): the introduction
// above the questions, and a submitted Snapshot leads to its report.

interface Question {
  block: InstrumentBlock;
  criterion: InstrumentCriterion;
}

type Loaded = {
  run: Run;
  questions: Question[];
  levels: ScaleLevel[];
  blocks: Map<string, BlockResponse>;          // by block key
  answers: Map<string, CriterionResponse>;     // by criterion key
};

type State = { kind: "loading" } | { kind: "error"; message: string } | { kind: "ready"; data: Loaded };

export function RespondSnapshot({ runId }: { runId: string }) {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const resumed = useRef(false);

  const load = useCallback(async () => {
    try {
      const run = await getRun(runId);
      if (!run) throw new Error("no assessment");
      const v = run.instrument_version_id;
      const [blocks, criteria, scales, blockRows, critRows] = await Promise.all([
        listInstrumentBlocks(v),
        listInstrumentCriteria(v),
        listScaleAnchors(v),
        listBlockResponses(run.id),
        listCriterionResponses(run.id),
      ]);
      const questions = blocks
        .slice()
        .sort((a, b) => a.sequence - b.sequence)
        .flatMap((block) =>
          criteria.filter((c) => c.instrument_block_id === block.id).map((criterion) => ({ block, criterion })),
        );
      const levels = [...scales.values()][0] ?? [];
      setState({
        kind: "ready",
        data: {
          run,
          questions,
          levels,
          blocks: new Map(blockRows.map((r) => [r.block_id, r])),
          answers: new Map(critRows.map((r) => [r.criterion_id, r])),
        },
      });
    } catch (e) {
      setState({ kind: "error", message: clientError("load", e).message });
    }
  }, [runId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Resume at the first unanswered question, once per visit.
  useEffect(() => {
    if (state.kind !== "ready" || resumed.current) return;
    resumed.current = true;
    const first = state.data.questions.findIndex((q) => answerOf(state.data.answers.get(q.criterion.criterion_key)) === null);
    setIndex(first === -1 ? Math.max(0, state.data.questions.length - 1) : first);
  }, [state]);

  async function choose(q: Question, answer: SnapshotAnswer) {
    if (state.kind !== "ready" || busy) return;
    const data = state.data;
    setBusy(true);
    setError(null);
    try {
      const blockKey = q.block.block_key;
      const block = data.blocks.get(blockKey) ?? (await createBlockResponse(data.run.organisation_id, data.run.id, blockKey));
      const key = q.criterion.criterion_key;
      const existing = data.answers.get(key) ?? (await createCriterionResponse(data.run.organisation_id, data.run.id, block.id, key));
      const updated = await updateCriterionResponse(existing.id, patchFor(answer));
      setState((prev) => {
        if (prev.kind !== "ready") return prev;
        const blocks = new Map(prev.data.blocks).set(blockKey, block);
        const answers = new Map(prev.data.answers).set(key, updated);
        return { kind: "ready", data: { ...prev.data, blocks, answers } };
      });
      // On to the next question, after a moment to see the choice.
      if (index < data.questions.length - 1) window.setTimeout(() => setIndex((i) => Math.min(i + 1, data.questions.length - 1)), 400);
    } catch (e) {
      setError(clientError("save", e).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (state.kind !== "ready") return;
    setSubmitting(true);
    setError(null);
    try {
      await submitSelfAssessment(state.data.run.id, state.data.run.state);
      navigate("/respond/report");
    } catch (e) {
      setError(clientError("submit", e).message);
      setSubmitting(false);
      setConfirming(false);
    }
  }

  if (state.kind === "loading") return <p>Loading your assessment…</p>;
  if (state.kind === "error") return <div className="error-banner" role="alert">{state.message}</div>;

  const { run, questions, levels, answers } = state.data;
  if (isSubmitted(run.state)) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Your Snapshot has been submitted</h2>
        <p>Thank you. Your answers are recorded and can no longer be changed.</p>
        <Link className="btn" to="/respond/report">Open your report</Link>
      </div>
    );
  }
  if (!isAnswering(run.state)) {
    return (
      <div className="card empty-state">
        <h3>This assessment is closed</h3>
        <p>It can no longer be answered. Please contact the person at LINNFOSS who invited you.</p>
      </div>
    );
  }
  const answered = questions.filter((q) => answerOf(answers.get(q.criterion.criterion_key)) !== null).length;
  const allAnswered = answered === questions.length;
  const q = questions[Math.min(index, questions.length - 1)];
  if (!q) return <div className="error-banner">This assessment has no questions. Please contact LINNFOSS.</div>;
  const current = answerOf(answers.get(q.criterion.criterion_key));
  const win = observationWindow(run.created_at);
  const examples = examplesOf(q.criterion.how_tested);
  const isChosen = (a: SnapshotAnswer) =>
    current !== null && (typeof a === "object" ? typeof current === "object" && current.score === a.score : current === a);

  const optionStyle = (selected: boolean) => ({
    display: "flex",
    gap: "0.6rem",
    alignItems: "flex-start",
    width: "100%",
    textAlign: "left" as const,
    padding: "0.45rem 0.6rem",
    borderRadius: "var(--radius)",
    border: `1px solid ${selected ? "var(--accent)" : "var(--border)"}`,
    background: selected ? "var(--accent-soft)" : "var(--surface-card)",
    color: "inherit",
    cursor: busy ? "wait" : "pointer",
  });

  return (
    <div>
      <h1 style={{ marginBottom: "0.25rem" }}>Transformation Snapshot</h1>
      <p style={{ color: "var(--text-secondary)", marginTop: 0 }}>
        {answered} of {questions.length} answered · saved as you go
      </p>
      <p>
        This Snapshot takes about five minutes. It asks how consistently {questions.length} transformation practices happened
        in the four weeks from <strong>{win.from}</strong> to <strong>{win.to}</strong>. Answer from what you have seen
        yourself; if you cannot tell, choose <em>Not known</em>. There are no right or wrong answers, and your answers are
        saved as you go.
      </p>

      {error && <div className="error-banner" role="alert">{error}</div>}

      <section className="card survey-card">
        <p style={{ margin: 0, fontSize: "var(--lf-meta-size)", color: "var(--text-muted)" }}>
          Question {index + 1} of {questions.length} · {q.block.title}
        </p>
        <h2 style={{ marginTop: "0.25rem" }}>{q.criterion.required_content}</h2>
        {examples.length > 0 && (
          <div style={{ fontSize: "var(--lf-body-size)", color: "var(--text-secondary)", margin: "0 0 0.6rem" }}>
            <em>For example:</em>
            <ul style={{ margin: "0.15rem 0 0", paddingLeft: "1.2rem" }}>
              {examples.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
        )}

        <div role="radiogroup" aria-label={q.block.title} style={{ display: "grid", gap: "0.4rem" }}>
          {levels.map((lv) => (
            <button
              key={lv.value}
              type="button"
              role="radio"
              aria-checked={isChosen({ score: lv.value })}
              disabled={busy || submitting}
              style={optionStyle(isChosen({ score: lv.value }))}
              onClick={() => void choose(q, { score: lv.value })}
            >
              <span>
                <strong>{lv.state}</strong>
                <span style={{ display: "block", fontSize: "var(--lf-body-size)", color: "var(--text-secondary)" }}>{lv.anchor}</span>
              </span>
            </button>
          ))}
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.2rem" }}>
            {UNSCORED.map((u) => (
              <button
                key={u.id}
                type="button"
                role="radio"
                aria-checked={isChosen(u.id)}
                disabled={busy || submitting}
                style={{ ...optionStyle(isChosen(u.id)), width: "auto", flex: "1 1 12rem" }}
                onClick={() => void choose(q, u.id)}
              >
                {u.label}
              </button>
            ))}
          </div>
        </div>
        <p aria-live="polite" style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)", margin: "0.4rem 0", visibility: busy ? "visible" : "hidden" }}>
          Saving…
        </p>

        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "space-between" }}>
          <button className="btn btn-secondary" disabled={index === 0} onClick={() => setIndex(index - 1)}>
            Previous
          </button>
          {index < questions.length - 1 ? (
            <button className="btn" onClick={() => setIndex(index + 1)}>Next</button>
          ) : (
            <button className="btn" disabled={!allAnswered || submitting} onClick={() => setConfirming(true)}>
              Review and submit
            </button>
          )}
        </div>
      </section>

      {index === questions.length - 1 && !allAnswered && (
        <p style={{ color: "var(--text-secondary)" }}>
          Every question needs an answer before you can submit. Still open:{" "}
          {questions
            .map((x, i) => (answerOf(answers.get(x.criterion.criterion_key)) === null ? i + 1 : null))
            .filter((n) => n !== null)
            .join(", ")}
          .
        </p>
      )}

      {confirming && (
        <div className="card" role="dialog" aria-modal="true" aria-labelledby="confirm-title" style={{ borderColor: "var(--accent)" }}>
          <h3 id="confirm-title" style={{ marginTop: 0 }}>Submit your Snapshot?</h3>
          <p>
            All {questions.length} questions have an answer. <strong>After you submit, your answers can no longer be
            changed.</strong> Your report is produced straight away.
          </p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button className="btn" disabled={submitting} onClick={() => void submit()}>
              {submitting ? "Submitting…" : "Submit"}
            </button>
            <button className="btn btn-secondary" disabled={submitting} onClick={() => setConfirming(false)}>
              Go back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
